import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { addIcons } from 'ionicons';
import {
  addCircleOutline, addOutline, alertCircleOutline, arrowBackOutline, cameraOutline, carOutline,
  chatbubbleEllipsesOutline, checkmarkCircleOutline, chevronBack, chevronForwardOutline,
  closeOutline, colorWandOutline, createOutline, earthOutline, ellipsisHorizontal, flagOutline, heart, heartOutline, informationCircleOutline,
  leafOutline, locationOutline, logOutOutline, mapOutline, navigateCircleOutline,
  navigateOutline, notificationsOutline, openOutline, optionsOutline, personAddOutline, personOutline, pieChartOutline,
  peopleOutline, personRemoveOutline, ribbonOutline, shareOutline, star, starHalf, starOutline, thumbsUp, thumbsUpOutline, trashOutline,
} from 'ionicons/icons';
import { ActivityApi } from './core/api/activity.api';
import { UsersApi } from './core/api/users.api';
import { authService } from './core/auth/auth.service';
import { getResolvedTimeZoneId } from './core/helpers/time-zone.helpers';
import { PushRegistrationService } from './core/services/push-registration.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private authStateSubscription?: Subscription;
  private readonly onSessionExpired = () => {
    void this.router.navigateByUrl('/login', { replaceUrl: true });
  };

  constructor(
    private readonly router: Router,
    private readonly nav: NavController,
    private readonly activityApi: ActivityApi,
    private readonly usersApi: UsersApi,
    private readonly zone: NgZone,
    private readonly pushRegistration: PushRegistrationService,
  ) {
    addIcons({
      addCircleOutline, addOutline, alertCircleOutline, arrowBackOutline, cameraOutline, carOutline,
      chatbubbleEllipsesOutline, checkmarkCircleOutline, chevronBack, chevronForwardOutline,
      closeOutline, colorWandOutline, createOutline, earthOutline, ellipsisHorizontal, flagOutline, heart, heartOutline, informationCircleOutline,
      leafOutline, locationOutline, logOutOutline, mapOutline, navigateCircleOutline,
      navigateOutline, notificationsOutline, openOutline, optionsOutline, personAddOutline, personOutline, pieChartOutline,
      peopleOutline, personRemoveOutline, ribbonOutline, shareOutline, star, starHalf, starOutline, thumbsUp, thumbsUpOutline, trashOutline,
    });
  }

  ngOnInit(): void {
    window.addEventListener('bm:session-expired', this.onSessionExpired);
    this.pushRegistration.initialize();

    if (Capacitor.isNativePlatform()) {
      void StatusBar.setOverlaysWebView({ overlay: false });
      void StatusBar.setStyle({ style: Style.Dark });
      void StatusBar.setBackgroundColor({ color: '#b8c9b5' });
    }

    if (authService.isAuthenticated()) {
      this.activityApi.trackAppOpened().subscribe({ error: () => {} });
    }

    this.authStateSubscription = authService.authState$.subscribe((isAuthenticated) => {
      if (isAuthenticated) this.syncTimeZone();
    });

    // Handle deep-link callbacks and Universal Links.
    App.addListener('appUrlOpen', async ({ url }) => {
      if (!url) return;

      if (url.startsWith('com.bevme.app://callback') || url.startsWith('com.bevmev1.app://callback')) {
        await Browser.close().catch(() => {});
        // OTP flow doesn't use redirect callbacks, but this keeps the hook
        // ready for any future Auth0 universal login integration.
        return;
      }

      this.handleUniversalLink(url);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('bm:session-expired', this.onSessionExpired);
    this.authStateSubscription?.unsubscribe();
    this.pushRegistration.destroy();
  }

  private syncTimeZone(): void {
    const timeZoneId = getResolvedTimeZoneId();
    if (!timeZoneId) return;

    this.usersApi.updateMyTimeZone(timeZoneId).subscribe({ error: () => {} });
  }

  private handleUniversalLink(url: string): void {
    this.zone.run(() => {
      try {
        const openedUrl = new URL(url);
        const path = openedUrl.pathname;

        if (openedUrl.hostname !== 'drinkbevme.com' && openedUrl.hostname !== 'www.drinkbevme.com') {
          return;
        }

        if (path.startsWith('/u/')) {
          const username = decodeURIComponent(path.split('/')[2] ?? '').trim();
          if (username) {
            void this.openUniversalLinkTarget(['/tabs/u', username]);
          }
          return;
        }

        if (path.startsWith('/p/')) {
          const placeId = decodeURIComponent(path.split('/')[2] ?? '').trim();
          if (placeId) {
            void this.openUniversalLinkTarget(['/tabs/explore/place', placeId]);
          }
        }
      } catch (error) {
        console.error('Unable to handle universal link', error);
      }
    });
  }

  private async openUniversalLinkTarget(commands: unknown[]): Promise<void> {
    await this.nav.navigateRoot('/tabs/explore', { animated: false });
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
    await this.nav.navigateForward(commands as string[], { animated: true });
  }
}
